import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productsApi } from '@/api/products';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft,
  Save,
  ShoppingBag,
} from 'lucide-react';

export function ProductFormPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const isEdit = !!code;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [productCode, setProductCode] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isEdit && code) {
      loadProduct(code);
    }
  }, [code, isEdit]);

  const loadProduct = async (productCode: string) => {
    try {
      setLoading(true);
      setError(null);
      const product = await productsApi.getByCode(productCode);
      setName(product.name);
      setProductCode(product.code);
      setDescription(product.description || '');
      setIsActive(product.isActive);
    } catch (err) {
      setError('Erreur lors du chargement du produit');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !productCode.trim()) {
      setError('Le nom et le code sont requis');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isEdit && code) {
        await productsApi.update(code, {
          name,
          description: description || undefined,
          isActive,
        });
        navigate(`/products/${code}`);
      } else {
        await productsApi.create({
          code: productCode,
          name,
          description: description || undefined,
          isActive,
        });
        navigate(`/products/${productCode}`);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/products" className="hover:text-foreground">Produits</Link>
        <span>/</span>
        <span className="text-foreground">{isEdit ? `Modifier ${name}` : 'Nouveau produit'}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" type="button" asChild>
              <Link to={isEdit ? `/products/${code}` : '/products'}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ShoppingBag className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              {isEdit ? `Modifier ${name}` : 'Nouveau produit'}
            </h1>
          </div>
        </div>

        <Button type="submit" disabled={saving}>
          <Save className="h-4 w-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            &times;
          </button>
        </div>
      )}

      {/* Form */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="text-base">Informations du produit</CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                value={productCode}
                onChange={(e) => setProductCode(e.target.value.toUpperCase())}
                placeholder="MYEDUCATION"
                disabled={isEdit}
                required
              />
              {isEdit && (
                <p className="text-xs text-muted-foreground">
                  Le code ne peut pas etre modifie
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nom *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="MyEducation"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description du produit..."
              rows={4}
            />
          </div>

          <div className="flex items-center justify-between space-x-2 pt-2">
            <div>
              <Label htmlFor="isActive">Produit actif</Label>
              <p className="text-sm text-muted-foreground">
                Un produit inactif ne sera pas proposé aux nouvelles écoles
              </p>
            </div>
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
